<script setup lang="ts">
import { useUiStore, IMPLEMENTED_TOOLS, type ToolId } from '@/stores/uiStore'
import { useI18n, type I18nKey } from '@/i18n'
import { TOOL_KEYS } from '@/composables/useShortcuts'
import ColorPicker from '@/components/common/ColorPicker.vue'
import { guideFor, tx } from '@/help/toolGuides'

const ui = useUiStore()
const { t } = useI18n()

interface ToolDef {
  id: ToolId
  label: I18nKey
  icon: string
}
const tools: ToolDef[] = [
  { id: 'move', label: 'tool.move', icon: '✥' },
  { id: 'marquee', label: 'tool.marquee', icon: '⬚' },
  { id: 'lasso', label: 'tool.lasso', icon: '⌒' },
  { id: 'wand', label: 'tool.wand', icon: '✧' },
  { id: 'quickSelect', label: 'tool.quickSelect', icon: '✦' },
  { id: 'crop', label: 'tool.crop', icon: '⌗' },
  { id: 'eyedropper', label: 'tool.eyedropper', icon: '💧' },
  { id: 'brush', label: 'tool.brush', icon: '🖌' },
  { id: 'spotHeal', label: 'tool.spotHeal', icon: '✚' },
  { id: 'heal', label: 'tool.heal', icon: '⚕' },
  { id: 'clone', label: 'tool.clone', icon: '⎘' },
  { id: 'eraser', label: 'tool.eraser', icon: '◻' },
  { id: 'blurTool', label: 'tool.blurTool', icon: '◌' },
  { id: 'sharpenTool', label: 'tool.sharpenTool', icon: '◭' },
  { id: 'smudge', label: 'tool.smudge', icon: '☜' },
  { id: 'dodge', label: 'tool.dodge', icon: '☼' },
  { id: 'burn', label: 'tool.burn', icon: '☾' },
  { id: 'sponge', label: 'tool.sponge', icon: '▦' },
  { id: 'fill', label: 'tool.fill', icon: '🪣' },
  { id: 'gradient', label: 'tool.gradient', icon: '▤' },
  { id: 'text', label: 'tool.text', icon: 'T' },
  { id: 'shape', label: 'tool.shape', icon: '▭' },
  { id: 'pen', label: 'tool.pen', icon: '✒' },
  { id: 'hand', label: 'tool.hand', icon: '✋' },
  { id: 'zoom', label: 'tool.zoom', icon: '🔍' }
]
function keyFor(id: ToolId): string {
  return Object.entries(TOOL_KEYS).find(([, v]) => v === id)?.[0]?.toUpperCase() ?? ''
}
function title(tl: ToolDef): string {
  const k = keyFor(tl.id)
  const head = `${t(tl.label)}${k ? ` (${k})` : ''}`
  if (!IMPLEMENTED_TOOLS.has(tl.id)) return `${head} ${t('tool.comingSoon')}`
  // ホバーでその場で分かるように、2 行目に 1 行の要約を出す
  return `${head}\n${tx(guideFor(tl.id).summary)}`
}
</script>

<template>
  <div class="toolbar">
    <button
      v-for="tl in tools"
      :key="tl.id"
      class="tool"
      :class="{ active: ui.effectiveTool === tl.id, soon: !IMPLEMENTED_TOOLS.has(tl.id) }"
      :title="title(tl)"
      :disabled="!IMPLEMENTED_TOOLS.has(tl.id)"
      @click="ui.activeTool = tl.id"
    >
      <span class="glyph">{{ tl.icon }}</span>
    </button>
    <div class="spacer" />
    <div class="colors">
      <button class="swatch bg" :style="{ background: ui.background }" :title="t('color.background')" @click="ui.colorPickerOpen = 'bg'" />
      <button class="swatch fg" :style="{ background: ui.foreground }" :title="t('color.foreground')" @click="ui.colorPickerOpen = 'fg'" />
      <button class="mini" :title="t('color.swap')" @click="ui.swapColors()">⇄</button>
      <button class="mini reset" :title="t('color.reset')" @click="ui.resetColors()">▪</button>
    </div>
    <ColorPicker v-if="ui.colorPickerOpen" :target="ui.colorPickerOpen" @close="ui.colorPickerOpen = null" />
  </div>
</template>

<style scoped>
.toolbar {
  background: var(--bg-1);
  border-right: 1px solid var(--line-region);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 0;
  gap: 2px;
  overflow: hidden auto;
}
.tool {
  width: 32px;
  height: 30px;
  padding: 0;
  border: 1px solid transparent;
  background: transparent;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}
.tool:hover:not(:disabled) {
  background: var(--bg-hover);
}
.tool.active {
  background: var(--bg-3);
  border-color: var(--line-strong);
  color: var(--accent-hi);
}
.tool.soon {
  opacity: 0.3;
}
.glyph {
  font-size: 15px;
  line-height: 1;
}
.colors {
  position: relative;
  width: 36px;
  height: 52px;
  margin-bottom: 6px;
}
.swatch {
  position: absolute;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--line-strong);
  border-radius: 2px;
}
.swatch.fg {
  left: 2px;
  top: 4px;
  z-index: 2;
  box-shadow: 0 0 0 1px var(--bg-1);
}
.swatch.bg {
  left: 12px;
  top: 14px;
}
.mini {
  position: absolute;
  width: 14px;
  height: 14px;
  padding: 0;
  font-size: 9px;
  line-height: 1;
  background: transparent;
  border-color: transparent;
  color: var(--fg-2);
}
.mini:not(.reset) {
  right: 0;
  top: 0;
}
.mini.reset {
  left: 0;
  bottom: 0;
}
</style>
